import App from '@/App.vue';
import config from '@/config';
import { changeLocale, getUserPreferredLocale, i18n } from '@/i18n';
import { navigationState, routes } from '@/router';
import { startupRequests, startupTrace } from '@/startup/startup-diagnostics';
import {
  registerStartupHook,
  reportStartupFailure,
  runStartupStep,
  withStartupTimeout,
} from '@/startup/startup-status';
import { useAuthStore } from '@/stores/authStore';
import { defineCustomElements } from '@scu/core-ui/dist/loader';
import { createAuthorizationGuard, createOAuthLoginGuard, defaultScrollBehavior } from '@sit/vue-core';
import { createPinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

import '@scu/core-ui/dist/schwarz-core-ui/schwarz-core-ui.css';
import '@sit/vue-core/style.css';
import { ErrorReason } from './utils/constants/error-reason-constants';
import {
  determineFunctionVisibility,
  PermissionFunction,
  setRequiredGroupsForRoutes,
} from './utils/permissions/permissions-utils';
import { hasParentId } from './utils/validation/type-guards';

/**
 * Setup up vue app. This function should not be called twice. Its exported only for testing.
 */

export async function setupApp() {
  registerStartupHook(window, config.auth.idp);
  const startedAt = Date.now();

  const currentStage = import.meta.env.ODJ_EE_STAGE.trim().toUpperCase();
  if (currentStage !== 'PROD') {
    document.title += ` (${currentStage})`;
  }

  // Initializing CoreUI
  // await applyPolyfills();
  await runStartupStep('Komponentenbibliothek', () => defineCustomElements());

  // const i18n = initializeI18n();
  const locale = getUserPreferredLocale();
  if (locale) await changeLocale(locale, false);

  const pinia = createPinia();
  pinia.use(piniaPluginPersistedState);
  const app = createApp(App).use(i18n).use(pinia);

  const authStore = useAuthStore();

  const router = createRouter({
    history: createWebHistory(config.app.baseUrl),
    scrollBehavior: defaultScrollBehavior,
    routes,
  });

  // Global before each guard to track navigation type
  router.beforeEach((to, from, next) => {
    if (navigationState.lastNavigationType === '') {
      navigationState.lastNavigationType = 'initial'; // Initial page load
    }

    next();
  });

  // Function to set navigation type
  const setNavigationType = (type: string) => {
    navigationState.lastNavigationType = type;
  };

  // Override push method to track navigation type
  const originalPush = router.push;
  router.push = (location) => {
    setNavigationType('push');
    return originalPush.call(router, location);
  };

  // Override back method to track navigation type
  const originalBack = router.back;
  router.back = () => {
    setNavigationType('back');
    return originalBack.call(router);
  };

  const isGoogleCallback = window.location.pathname === '/google/callback';

  if (!config.auth.disabled && !isGoogleCallback) {
    router.beforeEach(createOAuthLoginGuard({ authStore }));
    router.beforeEach(createAuthorizationGuard({ authStore }));
  }

  let requiredGroupsLoad: Promise<void> | null = null;
  const loadRequiredGroups = () => {
    if (!requiredGroupsLoad) {
      startupTrace.endStep('Anmeldung und Routing', 'abgeschlossen');
      startupTrace.beginStep('Rechteermittlung');
      requiredGroupsLoad = withStartupTimeout(setRequiredGroupsForRoutes(routes), 'Rechteermittlung', startedAt);
      requiredGroupsLoad.then(
        () => startupTrace.endStep('Rechteermittlung', 'abgeschlossen'),
        () => startupTrace.endStep('Rechteermittlung', 'fehlgeschlagen')
      );
    }
    return requiredGroupsLoad;
  };

  let requiredGroupsInitialized = false;
  let requiredGroupsInitPromise: Promise<void> | null = null;

  //for inserting an copied link check for permission
  router.beforeEach(async (to, from, next) => {
    if (!requiredGroupsInitialized) {
      if (!requiredGroupsInitPromise) {
        requiredGroupsInitPromise = loadRequiredGroups();
      }
      await requiredGroupsInitPromise;
      requiredGroupsInitialized = true;
    }

    if (to.meta.requiresAuth && to.path !== '/') {
      const parentFunction = Object.values(PermissionFunction).find((f) => f.pathRegex.test(to.path));
      const childFunctions = Object.values(PermissionFunction).filter((f) => {
        if (hasParentId(f)) {
          f.parentId === parentFunction?.id;
        }
      });

      let isVisible = false;

      if (childFunctions.length > 0) {
        const matchingChildFunction = childFunctions.find((f) => {
          function removeParentPath(toPath: string, parentRegex?: RegExp): string {
            let newPath = toPath;

            if (parentRegex != undefined) {
              const match = toPath.match(parentRegex);
              if (match) {
                newPath = toPath.slice(match[0].length).replace(/^\/+/, '');
              }
            }

            return newPath;
          }

          const childPath = removeParentPath(to.path, parentFunction?.pathRegex);

          return f.pathRegex.test(childPath);
        });

        isVisible = determineFunctionVisibility(matchingChildFunction, authStore);
      } else {
        isVisible = determineFunctionVisibility(parentFunction, authStore);
      }

      if (!isVisible) {
        return next({ name: 'Error', query: { reason: ErrorReason.Unauthorized } });
      }
    }

    next();
  });

  // Der Guard ruft loadRequiredGroups schon während app.use auf; der Schritt muss davor begonnen sein.
  startupTrace.beginStep('Anmeldung und Routing');

  // register the router after the before each. -> bug on gcp beforeEach don't get executed onload
  app.use(router);
  await runStartupStep('Anmeldung und Routing', () => router.isReady());
  /** setzen der "requiredGroups" für alle Routen
  (requiredGroups: muss die SIAM-Schlüssel der Rollen, die Schreib/Lese-Berechtigung für die jeweilige Route besitzen enthalten)
  */
  await runStartupStep('Rechteermittlung', loadRequiredGroups);

  app.mount('#app');
  return app;
}
setupApp().catch((error) => reportStartupFailure(error, document, startupTrace, startupRequests, config.auth.idp));
